import React, { useState, useContext, useEffect, useRef } from 'react';
import './Inside.scss';
import VideoSimple from '@/v2/components/media/video/VideoSimple';
import TimelineText from '@/v2/components/atoms/TimelineText';
import TimeController from '@/v2/components/controllers/TimeController';
import Button from '@/v2/components/atoms/Button';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import Header from '@/v2/components/application/Header';
import Music from '@/v2/components/atoms/Icon/Music';
import House from '@/v2/components/atoms/Icon/House';
import Navigate from '@/v2/components/atoms/Icon/Navigate';
import Message from '@/v2/components/atoms/Icon/Message';
import Dialog from '@/v2/components/layouts/Dialog';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { timeout, saveTimeout, clearTimeouts } from '@/v2/components/utils/Timeout';
import { ConnectivityContext } from '@/v2/state/providers';
import { StateContext } from '@/v2/state/store';
import { actionStrings, MomentStateContext } from '@/v2/state/momentState';
import Fade from '@/v2/components/atoms/Fade/Fade';

const IconComponentMap = {
    music: Music,
    home: House,
    navigate: Navigate,
    message: Message,
};

let videoController = null;
let timeController = null;
let visited = {};

const STATE = {
    intro: 'intro',
    icons: 'icons',
    end: 'end',
};

const CHANGE_DELAY = 1500;

const videoReady = (controller, onReady) => {
    videoController = controller;
    onReady();
};

const play = () => {
    videoController.Play();
};

const drawChat = (type, icons, names, show) => {
    if (!type) return '';

    const icon = icons.filter((item) => item.type === type)[0];

    return (
        <div className="Inside__chat">
            <Dialog bodyText={icon.label.chats[0].question} chatIconText={names.you} variant={type === 'home' ? 'alexa' : 'app'} show={show} />
            <Dialog bodyText={icon.label.chats[0].answer} chatResponse variant={type === 'home' ? 'alexa' : 'app'} show={show} />
        </div>
    );
};

const visitIcon = (type, setIcon) => {
    setIcon(type);
    visited[type] = true;
};

const inCarTrackEvent = (trackEvent, iconType) => {
    const iconName = iconType.toLowerCase();

    trackEvent('vcClick', {
        vcStep: 'inside',
        vcClick: `hotspot:${iconName}`,
    });
};

const isFirstVisited = () => Object.keys(visited).length === 0;

export default ({ isActive, data, onFinished, onReady }) => {
    const [state, setState] = useState(STATE.intro);
    const [icon, setIcon] = useState(null);
    const [continueButton, setContinueButton] = useState(false);
    const [showDialog, setShowDialog] = useState(true);
    const [showTimeLineText, setShowTimeLineText] = useState(false);
    const [visitedFeatures, setVisitedFeatures] = useState([]);

    const { trackEvent } = useContext(AnalyticsContext);
    const [{ disclaimer }] = useContext(ConnectivityContext);

    const store = useContext(StateContext);
    const {
        dispatch,
        state: { lightBgEnabled },
    } = store;

    const { dispatch: momentDispatch } = useContext(MomentStateContext);

    const time = useRef(TimelineText.Time());

    const end = () => {
        if (!lightBgEnabled) dispatch({ type: 'SET_BACKGROUND_LIGHT' });
        setState(STATE.end);

        timeout(() => {
            onFinished();
        }, 1000);
    };

    const activate = () => {
        timeController = new TimeController(time.current.Set);
        setShowTimeLineText(true);
        play();
        saveTimeout(
            timeController.AddTimeoutFromTimeline(data.text.timeline, () => {
                setTimeout(() => {
                    setState(STATE.icons);
                    setShowTimeLineText(false);
                    setTimeout(() => {
                        setContinueButton(true);
                    }, 800);
                }, 600);
            })
        );
    };

    useEffect(() => {
        visited = {};
        return () => clearTimeouts();
    }, []);

    useEffect(() => {
        if (isActive && videoController) {
            activate();
        }
    }, [isActive, videoController]);

    const options = {
        bufferBeforePlay: true,
        onReady: (controller) => videoReady(controller, onReady, time.current.Set),
        autoPlay: false,
        loop: true,
    };

    const onIconBtnClick = (itemType, itemsCount) => {
        // Delay only if user visited a section
        const dialogChangeTimeout = isFirstVisited() ? 0 : CHANGE_DELAY;

        // Animate out before switching sections
        if (!isFirstVisited()) {
            setShowDialog(false);
        }

        const visitedItems = [...new Set([...visitedFeatures, itemType])];
        setVisitedFeatures(visitedItems);
        momentDispatch({
            type: actionStrings.activeMomentProgress,
            payload: visitedItems.length * (100 / itemsCount),
        });

        timeout(() => {
            setShowDialog(true);
            visitIcon(itemType, setIcon);
            inCarTrackEvent(trackEvent, itemType);
        }, dialogChangeTimeout);
    };

    const onContinueBtnClick = () => {
        // setShowDialog(false);
        end(setState, lightBgEnabled, dispatch, onFinished);
    };

    const animate = state === STATE.end ? 'out' : 'in';

    const getIconComponent = (iconType) => {
        const IconComponent = IconComponentMap[iconType];
        return <IconComponent />;
    };

    const disclaimerContent = isActive && disclaimer ? <Disclaimer data={disclaimer} /> : '';

    return (
        <div className="Inside">
            <VideoSimple src={data.video} options={options} />
            <Header show={isActive} hideLogo />
            {isActive && (
                <>
                    {showTimeLineText && <TimelineText data={data.text.timeline} time={time.current} background="dark" color="white" />}
                    {state !== STATE.intro && (
                        <>
                            <div className="Inside__icons">
                                {data.icons.map((item) => (
                                    <Button
                                        variant="icon-light"
                                        iconSmall={false}
                                        icon={getIconComponent(item.type)}
                                        animate={animate}
                                        onClick={() => onIconBtnClick(item.type, data.icons.length)}
                                        className={['Inside__icon', `Inside__icon-${animate}`, visited[item.type] ? 'Inside__icon-visited' : ''].join(' ')}
                                        key={`icon-${item.type}`}
                                    />
                                ))}

                                {continueButton && (
                                    <div className="Inside__continue">
                                        <Button variant="secondary-light" animate={animate} onClick={onContinueBtnClick}>
                                            {data.text.cta.continue}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {drawChat(icon, data.icons, { you: data.text.cta.you, ariya: data.text.cta.ariya }, showDialog)}
                        </>
                    )}
                </>
            )}
            {disclaimerContent}

            <Fade show={!isActive || (isActive && animate === 'out')} light={animate === 'out'} dark={animate === 'in'} cover />
        </div>
    );
};
